import {
  Grid,
  Divider,
  TextField,
  Autocomplete,
  InputAdornment,
  Box,
  Tooltip,
  AutocompleteInputChangeReason,
} from "@mui/material";
import { useFormikContext } from "formik";
import InputField from "../../Reuseables/Fields/InputField";
import { ChangeEvent, useState } from "react";

import HelpIcon from "@mui/icons-material/Help";
import CreateProjectIntro from "../../Reuseables/Introductions/CreateProjectIntro";
import { useTranslation } from "react-i18next";
import SelectField from "../../Reuseables/Fields/SelectField";
import VisibilityGuard from "../../Reuseables/Guards/VisibilityGuard";
import MultiSelectField from "../../Reuseables/Fields/MultiSelectField";
import { useMemo } from "react";
import formModel from "../formModel";
import { Anonymity } from "../../../types/enums";
import { Project } from "../../../types/project";
import { getTypesWithDisabledProperty } from "../../../helpers/utils";
import {
  anonymityOptions,
  deviceTypeOptions,
  industries,
} from "../../../helpers/selectOptions";
import { useOrganizationContext } from "../../ContextProviders/OrganizationContext";
import {
  CityCountryPrediction,
  getPlaceDetails,
  PredictCityCountry,
} from "../../../helpers/GoogleAutoComplete";

export default function NameAndLocationStep() {
  const { t } = useTranslation();
  const { activeWorkspace } = useOrganizationContext();
  const {
    formField: {
      anonymity,
      deviceType,
      projectName,
      description,
      industry,
      location,
    },
  } = formModel;
  const { setFieldValue, values, touched, errors } =
    useFormikContext<Project>();
  const [locationField, setLocationField] = useState<CityCountryPrediction[]>(
    []
  );

  const getAvailableDeviceTypes = useMemo(() => {
    return getTypesWithDisabledProperty(
      activeWorkspace.availableDeviceTypes,
      deviceTypeOptions
    );
  }, [activeWorkspace.availableDeviceTypes]);

  const handleChangeLocation = async (
    searchValue: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!searchValue.target.value) {
      return null;
    }
    const results = await PredictCityCountry(searchValue.target.value);
    if (results) {
      setLocationField(results);
    }
  };

  const changeLocation = async (
    _: React.SyntheticEvent,
    value: string,
    __: AutocompleteInputChangeReason
  ) => {
    let find = locationField.find((val) => val?.suggestion === value);

    if (!find?.placeId) {
      return;
    }
    setFieldValue("location", value);

    let places = await getPlaceDetails(find.placeId);

    if (!places) return;
    if (!places.place.addressComponents) return;
    for (let i = 0; i < places.place.addressComponents.length; i++) {
      if (places.place.addressComponents[i]?.types[0] === "country") {
        if (places.place.addressComponents[i]?.shortText === "US") {
          setFieldValue("storageRegion", 2);
        } else {
          setFieldValue("storageRegion", 1);
        }
      }
    }
  };

  return (
    <Grid
      container
      mt={4}
      sx={{
        flexDirection: {
          xs: "column",
          sm: "column",
          md: "row",
          lg: "row",
        },
      }}
    >
      <Grid item xs>
        <Grid container>
          <Grid item flexGrow={1} />
          <Grid item xs={12} sm={8} md={10} lg={10} xl={8}>
            <CreateProjectIntro />
          </Grid>
          <Grid item flexGrow={1} />
        </Grid>
      </Grid>
      <Divider orientation="vertical" flexItem />
      <Grid item xs>
        <Grid container>
          <Grid item flexGrow={1} />
          <Grid
            item
            xs={12}
            sm={8}
            md={10}
            lg={10}
            xl={8}
            sx={{
              "& .MuiBox-root": { mb: 2 },
            }}
          >
            <Box>
              <InputField
                name={projectName.name}
                label={projectName.label}
                placeholder={projectName.placeholder}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={projectName.infoMsg}>
                        <HelpIcon style={{ cursor: "default" }} />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box>
              <InputField
                name={description.name}
                label={description.label}
                placeholder={description.placeholder}
                multiline
                rows={5}
                sx={{ "& 	.MuiInputAdornment-root": { mb: 12 } }}
                inputProps={{ spellCheck: false }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={description.infoMsg}>
                        <HelpIcon style={{ cursor: "default" }} />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box>
              <SelectField
                fullWidth
                label={industry.label}
                name={industry.name}
                data={industries}
                infoMsg={industry.infoMsg}
              />
            </Box>
            <Box>
              <Autocomplete
                freeSolo
                clearOnEscape
                options={locationField.map((option) => option?.suggestion)}
                onInputChange={changeLocation}
                value={values.location as string}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t(location.placeholder)}
                    error={Boolean(touched.location && errors.location)}
                    helperText={
                      touched.location && errors.location
                        ? t(errors.location)
                        : null
                    }
                    name={location.name}
                    label={t(location.label)}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.endAdornment}
                          <InputAdornment position="end">
                            <Tooltip title={location.infoMsg}>
                              <HelpIcon style={{ cursor: "default" }} />
                            </Tooltip>
                          </InputAdornment>
                        </>
                      ),
                    }}
                    onChange={handleChangeLocation}
                    variant="outlined"
                  />
                )}
              />
            </Box>
            <VisibilityGuard
              requestedProperties={[activeWorkspace.anonymity]}
              allowedProperties={[Anonymity.Known]}
            >
              <Box>
                <SelectField
                  fullWidth
                  name={anonymity.name}
                  label={anonymity.label}
                  data={anonymityOptions}
                  infoMsg={anonymity.infoMsg}
                />
              </Box>
            </VisibilityGuard>
            <Box>
              <MultiSelectField
                data={getAvailableDeviceTypes}
                name={deviceType.name}
                label={deviceType.label}
                infoMsg={deviceType.infoMsg}
              />
            </Box>
          </Grid>
          <Grid item flexGrow={1} />
        </Grid>
      </Grid>
    </Grid>
  );
}
